import type * as Y from 'yjs';
import { layerSplitChanges } from '../model/layerSplit.js';
import { itemTidyChanges } from '../model/itemTidy.js';
import { trayItemChanges } from '../model/areas.js';
import { createInkLayer, INK_LAYER_ID } from '../model/layers.js';
import { SCHEMA_VERSION } from '../model/schema.js';
import { collection, getMeta, metaMap, upsert } from './ydoc.js';

/**
 * Brings a live board Y.Doc to the current structure – the server runs it when a document is
 * loaded, before any client syncs: the OIL-51 layer split (v3), the item tidy-up of v4 (unique
 * labels, stale catalog references) and the v5 tray flag (OIL-281; a v1 doc also gets its ink
 * layer, so the version can move to 5 and the tray step never runs twice). Idempotent; an empty
 * doc stays untouched. Returns whether something changed.
 */
export function upgradeBoardDoc(doc: Y.Doc, origin?: unknown): boolean {
  const layers = collection(doc, 'layers');
  if (layers.size === 0) return false;
  const changes = layerSplitChanges({
    layers: Object.fromEntries(layers.entries()),
    items: Object.fromEntries(collection(doc, 'items').entries()),
  });
  const split = new Map(changes.items.map((i) => [i.id, i]));
  const items = {
    ...Object.fromEntries(collection(doc, 'items').entries()),
    ...Object.fromEntries(split),
  };
  const tidy = itemTidyChanges(items);
  for (const i of tidy) split.set(i.id, i);
  const v = getMeta(doc).schemaVersion;
  // before v5 `frameId: null` meant "in the tray"; afterwards it can mean "free in the world"
  const tray = v < 5 ? trayItemChanges({ ...items, ...Object.fromEntries(split) }) : [];
  for (const i of tray) split.set(i.id, i);
  // v2+ docs are exactly "current minus these changes"; a v1 doc only misses the ink layer too
  const outdated = v < SCHEMA_VERSION;
  const inkMissing = v === 1 && !layers.has(INK_LAYER_ID);
  if (changes.layers.length === 0 && split.size === 0 && !outdated) return false;
  doc.transact(() => {
    for (const l of changes.layers) upsert(doc, 'layers', l);
    // written directly: the labels are already unique, upsert must not renumber them one by one
    for (const i of split.values()) collection(doc, 'items').set(i.id, i);
    if (inkMissing) {
      const orders = [...layers.values()].map((l) => l.order);
      upsert(doc, 'layers', createInkLayer(Math.max(-1, ...orders) + 1));
    }
    if (outdated) metaMap(doc).set('schemaVersion', SCHEMA_VERSION);
  }, origin);
  return true;
}
